import * as React from 'react'
import { Query } from 'react-apollo'
import { RouteComponentProps } from 'react-router-dom'

import Loader from 'main/components/common/Loader'
import Page from 'main/components/common/Page'
import PageContent from 'main/components/common/PageContent'
import ContactSchoolModal from 'main/components/lead/ContactSchoolModal'
import { IWhitelabelContext, useWhitelabel } from 'main/framework'
import {
  EXTERNAL_SCHOOL_QUERY,
  ExternalSchool as ExternalSchoolData,
  ExternalSchool_externalSchool as IExternalSchool,
  ExternalSchoolVariables,
} from 'main/gql/externalSchool'
import { Button, Hidden, Link, TabGroup, Visible } from 'main/ui'
import { determineImage } from 'main/util/school'
import { ITabOption } from 'main/util/tab'
import segment from 'util/analytics/segment'

import Basics from 'main/components/school/Basics'
import Section from 'main/components/school/common/Section'
import DirectorProfile from 'main/components/school/DirectorProfile'
import Location from 'main/components/school/Location'
import RelatedPrograms from 'main/components/school/RelatedPrograms'
import { SchoolListingType } from 'main/gql/globalTypes'

import { ClaimAccount, Description, Hero, HotlineInfoCta, Tuition, Warning } from './Content'
import JoinModal from './modals/JoinModal'
import RemoveModal from './modals/RemoveModal'
import { Modal } from './types'

import {
  ContentContainer,
  DirectorProfileContainer,
  HeroContainer,
  LeftColumn,
  RequestInfoContainer,
  RightColumn,
  TabGroupContainer,
} from './ExternalSchool.styles'

type TabSelection = 'basics' | 'about'

// NOTE(tyu): It's not guaranteed that users going through conversion flows and
// triggering segment events on external school page already have a ws account
// and hence been identified. To allow customer io campaigns to trigger for
// anonymous users, include `recipient` field in payload and use in TO field.
// Reference: https://customer.io/docs/anonymous-invite-emails
export const SLACK_NOTIFICATION_CHANNEL = '#bell-external-schools'

const TAB_OPTIONS: Array<ITabOption<TabSelection>> = [
  { label: 'Basics', value: 'basics' },
  { label: 'About', value: 'about' },
]

export default function ExternalSchool({
  match,
}: RouteComponentProps<{ externalSchoolId: string }>) {
  const externalSchoolId = Number(match.params.externalSchoolId)

  const whitelabel = useWhitelabel()

  return (
    <Query<ExternalSchoolData, ExternalSchoolVariables>
      query={EXTERNAL_SCHOOL_QUERY}
      variables={{ id: externalSchoolId }}
    >
      {({ data, error, loading }) => (
        <Page headerParams={{ alwaysShowSearchForm: true }} {...generatePageProps(data)}>
          {loading || error || !data || !data.externalSchool ? (
            <Loader />
          ) : (
            <Content externalSchool={data.externalSchool} whitelabel={whitelabel} />
          )}
        </Page>
      )}
    </Query>
  )
}

interface IContentProps {
  externalSchool: IExternalSchool
  whitelabel: IWhitelabelContext
}

interface IContentState {
  currentTab: TabSelection
  modal: Modal | null
}

type ContentGroup =
  | 'director-profile'
  | 'external-school-disclaimer'
  | 'basics-provider-info'
  | 'descriptions'
  | 'tab-selector'
  | 'request-info-cta'
  | 'tuition-and-location'
  | 'related-programs'
  | 'join-wonderschool'
  | 'de-list-program'

class Content extends React.Component<IContentProps, IContentState> {
  public state = {
    currentTab: 'basics' as TabSelection,
    modal: null,
  }

  public render() {
    const { externalSchool } = this.props
    const { headerImageUrl, name } = externalSchool
    const { modal } = this.state

    const imgUrl = determineImage(false, externalSchool.id, this.props.whitelabel, headerImageUrl)

    const backgroundImageUrl = `${imgUrl}/-/format/auto/-/resize/1280x/`

    return (
      <>
        <HeroContainer>
          <Hero
            backgroundImageUrl={backgroundImageUrl}
            schoolName={name}
            onClickRequestInfo={() => this.openModal('contact_school')}
          />
        </HeroContainer>

        <PageContent>
          <ContentContainer>
            <Visible lg xl>
              {this.renderDesktop()}
            </Visible>
            <Hidden lg xl>
              {this.renderMobile()}
            </Hidden>
          </ContentContainer>
        </PageContent>

        {modal && this.renderModal(modal)}
      </>
    )
  }

  private handleRequestInfoComplete = (callbackData?: {}) => {
    segment.track('Click External School Request Info Button', {
      ...callbackData,
      recipient: SLACK_NOTIFICATION_CHANNEL,
    })
  }

  private renderDesktop = () => {
    return (
      <>
        <LeftColumn>
          {this.renderContent('director-profile')}
          {this.renderContent('basics-provider-info')}
          {this.renderContent('descriptions')}
          {this.renderContent('join-wonderschool')}
        </LeftColumn>
        <RightColumn>
          {this.renderContent('request-info-cta')}
          {this.renderContent('tuition-and-location')}
          {this.renderContent('related-programs')}
          {this.renderContent('de-list-program')}
        </RightColumn>
      </>
    )
  }

  private renderMobile = () => {
    const { currentTab } = this.state

    return (
      <>
        {this.renderContent('director-profile')}
        {this.renderContent('tab-selector')}
        {this.renderContent('request-info-cta')}
        {currentTab === 'basics' && this.renderContent('tuition-and-location')}
        {currentTab === 'basics' && this.renderContent('basics-provider-info')}
        {currentTab === 'about' && this.renderContent('descriptions')}
        {this.renderContent('join-wonderschool')}
        {this.renderContent('related-programs')}
        {this.renderContent('de-list-program')}
      </>
    )
  }

  private renderModal = (modal: Modal | null) => {
    const { externalSchool } = this.props

    switch (modal) {
      case 'join':
        return (
          <JoinModal
            externalSchool={externalSchool}
            onClickRemove={() => this.openModal('remove')}
            onRequestClose={this.closeModal}
          />
        )
      case 'remove':
        return <RemoveModal externalSchool={externalSchool} onRequestClose={this.closeModal} />
      case 'contact_school':
        return (
          <ContactSchoolModal
            externalSchoolId={externalSchool.id}
            onComplete={this.handleRequestInfoComplete}
            onRequestClose={this.closeModal}
          />
        )
    }
  }

  private renderContent = (contentGroup: ContentGroup) => {
    const { externalSchool } = this.props
    const {
      directorAvatarUrl,
      directorFirstName,
      directorLastName,
      locationDescription,
      philosophyDescription,
      profile,
      teacherDescription,
    } = externalSchool

    switch (contentGroup) {
      case 'director-profile':
        if (!directorFirstName && !directorLastName) return null
        return (
          <DirectorProfileContainer>
            <DirectorProfile
              avatarUrl={directorAvatarUrl}
              firstName={directorFirstName}
              lastName={directorLastName}
            />
          </DirectorProfileContainer>
        )

      // Currently not rendered as not needed with NC listings.
      // Consider to render it again or completely delete
      // the module if not needed anymore.
      case 'external-school-disclaimer':
        return (
          <Section>
            <Warning onClickJoin={() => this.openModal('join')} />
          </Section>
        )

      case 'basics-provider-info':
        return <Section>{profile && <Basics school={externalSchool} />}</Section>

      case 'descriptions':
        return (
          <>
            {!!philosophyDescription && (
              <Section>
                <Description title="Philosophy" description={philosophyDescription} />
              </Section>
            )}
            {!!locationDescription && (
              <Section>
                <Description title="Location" description={locationDescription} />
              </Section>
            )}
            {!!teacherDescription && (
              <Section>
                <Description title="Our Teacher" description={teacherDescription} />
              </Section>
            )}
          </>
        )

      case 'join-wonderschool':
        return (
          <Section>
            <ClaimAccount school={externalSchool} />
          </Section>
        )

      case 'tab-selector':
        return (
          <TabGroupContainer>
            <TabGroup<TabSelection>
              full
              outline
              options={TAB_OPTIONS}
              value={this.state.currentTab}
              onChange={currentTab => this.setState({ currentTab })}
            />
          </TabGroupContainer>
        )

      case 'request-info-cta':
        return (
          <RequestInfoContainer>
            <Button full onClick={() => this.openModal('contact_school')}>
              Message
            </Button>
            <HotlineInfoCta />
          </RequestInfoContainer>
        )

      case 'tuition-and-location':
        return (
          <>
            <Section>
              <Tuition onClickTuitionInfo={() => this.openModal('contact_school')} />
            </Section>
            <Section>
              <Location address={externalSchool.address} />
            </Section>
          </>
        )

      case 'related-programs':
        return <RelatedPrograms school={externalSchool} type={SchoolListingType.EXTERNAL} />

      case 'de-list-program':
        return (
          <Section>
            <Link to="#" onClick={() => this.openModal('remove')}>
              Not interested in joining Wonderschool? Request to have your listing removed here.
            </Link>
          </Section>
        )
    }
  }

  private closeModal = () => this.setState({ modal: null })

  private openModal = (modal: Modal) => this.setState({ modal })
}

function generatePageProps(data?: ExternalSchoolData) {
  if (!data || !data.externalSchool) return {}

  const { externalSchool } = data
  const { name, address } = externalSchool

  const pageTitle = `${name} | Child Care in ${address.city}`
  const pageDescription = `${name} is a child care program in ${address.city}. Learn more.`
  const pageMetatags = [
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
  ]

  return {
    description: pageDescription,
    metatags: pageMetatags,
    title: pageTitle,
  }
}
